import { api } from '../../../api/client';

const BASE = '/monitoring';

export const monitoringApi = {
    // METRICS
    getOverview: () =>
        api.get(`${BASE}/metrics/overview`),

    getTimeline: (hours = 24) =>
        api.get(`${BASE}/metrics/timeline?hours=${hours}`),

    getByModule: () =>
        api.get(`${BASE}/metrics/by-module`),

    getTopUsers: (limit = 10) =>
        api.get(`${BASE}/metrics/by-user?limit=${limit}`),

    getServiceHealth: () =>
        api.get(`${BASE}/metrics/service-health`),

    // LOGS NOC
    /**
    * Retrieves paginated audit logs with optional filters.
     * @param {Object} params - { module, status, username, since, until, page, size }
     */
    getLogs: (params = {}) => {
        const qs = new URLSearchParams(
            Object.fromEntries(
                Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
            )
        ).toString();
        return api.get(`${BASE}/logs${qs ? `?${qs}` : ''}`);
    },

    /**
    * Retrieves recent errors for the NOC panel.
     * @param {Object} params - { module, hours, page, size }
     */
    getRecentErrors: (params = {}) => {
        const qs = new URLSearchParams(
            Object.fromEntries(
                Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
            )
        ).toString();
        return api.get(`${BASE}/logs/errors${qs ? `?${qs}` : ''}`);
    },

    // ALERT RULES
    getAlertRules: () =>
        api.get(`${BASE}/alerts/rules`),

    createAlertRule: (data) =>
        api.post(`${BASE}/alerts/rules`, data),

    updateAlertRule: (id, data) =>
        api.put(`${BASE}/alerts/rules/${id}`, data),

    deleteAlertRule: (id) =>
        api.delete(`${BASE}/alerts/rules/${id}`),

    // ALERT EVENTS
    getAlertEvents: (params = {}) => {
        const qs = new URLSearchParams(
            Object.fromEntries(
                Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
            )
        ).toString();
        return api.get(`${BASE}/alerts/events${qs ? `?${qs}` : ''}`);
    },

    getActiveAlerts: () =>
        api.get(`${BASE}/alerts/events/active`),

    acknowledgeAlert: (id) =>
        api.put(`${BASE}/alerts/events/${id}/acknowledge`, {}),

    resolveAlert: (id) =>
        api.put(`${BASE}/alerts/events/${id}/resolve`, {}),

    // SSE STREAM
    /**
    * Creates an SSE connection for real-time updates.
     * @param {function} onMessage - callback(eventName, data)
     * @param {function} onError - callback(error)
    * @returns {EventSource} — Call .close() to disconnect.
     */
    createSseConnection: (onMessage, onError) => {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const token = sessionStorage.getItem('token');
        const url = `${apiUrl}/api/monitoring/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`;

        const eventSource = new EventSource(url);

        eventSource.onmessage = (e) => {
            try {
                const data = JSON.parse(e.data);
                onMessage('message', data);
            } catch {
                onMessage('message', e.data);
            }
        };

        // Listen for named SSE events.
        ['metrics', 'alert', 'heartbeat'].forEach(name => {
            eventSource.addEventListener(name, (e) => {
                try {
                    const data = JSON.parse(e.data);
                    onMessage(name, data);
                } catch {
                    onMessage(name, e.data);
                }
            });
        });

        eventSource.onerror = (e) => {
            if (onError) onError(e);
        };

        return eventSource;
    },

    // NOC INCIDENTS
    /**
    * Returns all incident notes for a specific log_id.
     * @param {number} logId
     */
    getNotesForLog: (logId) =>
        api.get(`${BASE}/incidents/log/${logId}`),

    /**
    * Returns incident notes by module and action.
     * @param {string} module
     * @param {string} action
     */
    getNotesForAction: (module, action) => {
        const qs = new URLSearchParams({ module, action }).toString();
        return api.get(`${BASE}/incidents/action?${qs}`);
    },

    /**
    * Returns the count of open incidents (OPEN + INVESTIGATING).
     */
    getOpenIncidentCount: () =>
        api.get(`${BASE}/incidents/open-count`),

    /**
    * Creates a new incident note.
     * @param {Object} data - { logId, module, action, note, correctiveAction, status, operatorUsername }
     */
    createIncidentNote: (data) =>
        api.post(`${BASE}/incidents`, data),

    /**
    * Updates an existing incident note.
     * @param {number} id
     * @param {Object} data - { note, correctiveAction, status }
     */
    updateIncidentNote: (id, data) =>
        api.put(`${BASE}/incidents/${id}`, data),

    /**
    * Deletes an incident note.
     * @param {number} id
     */
    deleteIncidentNote: (id) =>
        api.delete(`${BASE}/incidents/${id}`),
};
